import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { postMultipart } from '../../graphql/restClient';
import { getAndroidKeystoreStatus } from '../../lib/platformApi';

/**
 * Android signing — uploads a `.keystore` / `.jks` to the org's
 * Secrets Manager bucket via platform-service, alongside the alias +
 * passwords needed by gradle's release signing config.
 *
 * The build script (forge-build.sh) pulls this blob just before
 * invoking `fastlane android build`, so the keystore never lives on
 * disk anywhere except the runner's temp WORKDIR for the duration of
 * a single build.
 *
 * The status endpoint never returns the keystore bytes / passwords —
 * only the alias + filename so the UI can show "configured" without
 * re-prompting. Replace flow blanks both fields and re-uploads.
 */
export function AndroidSigningSection() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [alias, setAlias] = useState('');
  const [password, setPassword] = useState('');
  const [keyPassword, setKeyPassword] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['android-keystore-status'],
    queryFn: getAndroidKeystoreStatus,
  });

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pick a keystore file first.');
      if (!alias.trim()) throw new Error('Key alias is required.');
      if (!password) throw new Error('Keystore password is required.');
      const fd = new FormData();
      fd.append('keystore', file);
      fd.append('alias', alias.trim());
      fd.append('password', password);
      if (keyPassword) fd.append('keyPassword', keyPassword);
      return postMultipart<{ uploaded: true; alias: string; filename?: string }>(
        '/build/credentials/android-keystore',
        fd,
      );
    },
    onSuccess() {
      setError(null);
      setFile(null);
      setAlias('');
      setPassword('');
      setKeyPassword('');
      setShowForm(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['android-keystore-status'] });
    },
    onError(err: Error) {
      setError(err.message);
    },
  });

  const configured = status.data?.uploaded === true;

  return (
    <section style={s.card}>
      <h2 style={s.cardTitle}>Android Signing</h2>
      <p style={s.cardHint}>
        Used to sign the release APK. Stored encrypted in Secrets Manager
        (one keystore per org). The build pipeline never persists the
        keystore on disk outside a single build's working directory.
      </p>

      {status.isLoading && <p style={s.muted}>Loading status…</p>}

      {!showForm && configured && (
        <div style={s.statusRow}>
          <span style={s.statusDot} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={s.statusName}>Keystore configured</div>
            <div style={s.statusMeta}>
              alias <code>{status.data?.alias}</code>
              {status.data?.filename ? ` · ${status.data.filename}` : ''}
              {status.data?.uploadedAt
                ? ` · uploaded ${new Date(status.data.uploadedAt).toLocaleString()}`
                : ''}
            </div>
          </div>
          <button type="button" onClick={() => setShowForm(true)} style={s.replaceBtn}>
            Replace
          </button>
        </div>
      )}

      {!showForm && !configured && !status.isLoading && (
        <button type="button" onClick={() => setShowForm(true)} style={s.uploadBtn}>
          Upload keystore
        </button>
      )}

      {showForm && (
        <div style={s.form}>
          <Field label="Keystore file (.jks / .keystore)">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jks,.keystore,application/octet-stream"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              style={s.fileInput}
            />
          </Field>
          <Field label="Key alias">
            <input
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="upload"
              style={s.input}
            />
          </Field>
          <Field label="Keystore password">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={s.input}
            />
          </Field>
          <Field
            label="Key password (optional)"
            hint="Leave blank if your key password matches the keystore password."
          >
            <input
              type="password"
              value={keyPassword}
              onChange={(e) => setKeyPassword(e.target.value)}
              style={s.input}
            />
          </Field>
          <div style={s.actions}>
            <button
              type="button"
              onClick={() => upload.mutate()}
              disabled={upload.isPending}
              style={s.uploadBtn}
            >
              {upload.isPending ? 'Saving…' : 'Save signing credentials'}
            </button>
            {configured && (
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setError(null);
                }}
                style={s.cancelBtn}
              >
                Cancel
              </button>
            )}
            {error && <span style={s.error}>{error}</span>}
          </div>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={s.field}>
      <span style={s.fieldLabel}>{label}</span>
      {children}
      {hint && <span style={s.fieldHint}>{hint}</span>}
    </label>
  );
}

const s: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: 20,
    marginBottom: 16,
  },
  cardTitle: { margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#0F172A' },
  cardHint: { margin: '0 0 16px', fontSize: 12, color: '#94A3B8' },
  muted: { fontSize: 12, color: '#94A3B8' },
  statusRow: { display: 'flex', alignItems: 'center', gap: 10 },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#10B981',
    flexShrink: 0,
  },
  statusName: { fontSize: 13, fontWeight: 600, color: '#0F172A' },
  statusMeta: { fontSize: 11, color: '#64748B', marginTop: 2 },
  replaceBtn: {
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#4F46E5',
    backgroundColor: '#fff',
    border: '1px solid #C7D2FE',
    borderRadius: 6,
    cursor: 'pointer',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: { fontSize: 12, fontWeight: 600, color: '#475569' },
  fieldHint: { fontSize: 11, color: '#94A3B8' },
  input: {
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid #CBD5E1',
    borderRadius: 4,
  },
  fileInput: { padding: '4px 0', fontSize: 12 },
  actions: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 },
  uploadBtn: {
    padding: '10px 16px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '10px 14px',
    fontSize: 13,
    color: '#64748B',
    backgroundColor: 'transparent',
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    cursor: 'pointer',
  },
  error: { fontSize: 12, color: '#c00' },
};
